// Story 11.2 — Tipos de nodo custom para Reactflow. Cada node component
// recibe `data: { label, detail, highlighted }` desde Reactflow y delega
// al NodeShell. Story 11.3 agrega `highlighted` para sync con el cursor.

import { Clock, Component, Database, FileType, GitFork, MessageSquare } from 'lucide-react';
import { memo } from 'react';
import { NodeShell } from './NodeShell';

interface NodeData {
  label: string;
  detail?: string;
  /** Story 11.3 — true cuando el cursor del editor cae en este step. */
  highlighted?: boolean;
}

interface NodeProps {
  data: NodeData;
}

const Message = memo(({ data }: NodeProps) => (
  <NodeShell
    title={data.label}
    detail={data.detail}
    icon={<MessageSquare className="h-3.5 w-3.5" aria-hidden />}
    bgClass="bg-info/15"
    borderClass="border-info/40"
    // BUG-FIX: `text-info-foreground` (oklch 0.13, casi negro) sobre el
    // tinte 15% del fondo de la pagina hacia el texto invisible. Patron
    // consistente con los demas nodos: usar la color semantica (text-info)
    // como texto, contrasta sobre el tinte y matchea Condition/DbAction/etc.
    textClass="text-info"
    highlighted={data.highlighted}
  />
));
Message.displayName = 'MessageNode';

const Wait = memo(({ data }: NodeProps) => (
  <NodeShell
    title={data.label}
    detail={data.detail}
    icon={<Clock className="h-3.5 w-3.5" aria-hidden />}
    bgClass="bg-muted"
    borderClass="border-border"
    textClass="text-muted-foreground"
    highlighted={data.highlighted}
  />
));
Wait.displayName = 'WaitNode';

const Condition = memo(({ data }: NodeProps) => (
  <NodeShell
    title={data.label}
    detail={data.detail}
    icon={<GitFork className="h-3.5 w-3.5" aria-hidden />}
    bgClass="bg-warning/15"
    borderClass="border-warning/40"
    textClass="text-warning"
    diamond
    highlighted={data.highlighted}
  />
));
Condition.displayName = 'ConditionNode';

const DbAction = memo(({ data }: NodeProps) => (
  <NodeShell
    title={data.label}
    detail={data.detail}
    icon={<Database className="h-3.5 w-3.5" aria-hidden />}
    bgClass="bg-success/15"
    borderClass="border-success/40"
    textClass="text-success"
    highlighted={data.highlighted}
  />
));
DbAction.displayName = 'DbActionNode';

const TsAction = memo(({ data }: NodeProps) => (
  <NodeShell
    title={data.label}
    detail={data.detail}
    icon={<FileType className="h-3.5 w-3.5" aria-hidden />}
    bgClass="bg-warning/15"
    borderClass="border-warning/40"
    textClass="text-warning"
    highlighted={data.highlighted}
  />
));
TsAction.displayName = 'TsActionNode';

const ComponentNode = memo(({ data }: NodeProps) => (
  <NodeShell
    title={data.label}
    detail={data.detail}
    icon={<Component className="h-3.5 w-3.5" aria-hidden />}
    bgClass="bg-primary/15"
    borderClass="border-primary/40"
    textClass="text-primary"
    highlighted={data.highlighted}
  />
));
ComponentNode.displayName = 'ComponentNode';

const GotoGhost = memo(({ data }: NodeProps) => (
  <NodeShell
    title={data.label}
    detail={data.detail}
    bgClass="bg-card"
    borderClass="border-border"
    textClass="text-muted-foreground"
    ghost
  />
));
GotoGhost.displayName = 'GotoGhostNode';

export const nodeTypes = {
  'send-message': Message,
  wait: Wait,
  condition: Condition,
  'db-action': DbAction,
  'ts-action': TsAction,
  component: ComponentNode,
  'goto-ghost': GotoGhost,
} as const;
